import { Component, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';
import { Property, PaymentPlan } from '../../../core/models/property.model';
import { LucideIconsModule } from '../../lucide-icons.module';
import { CurrencyService } from '../../../core/services/currency.service';
import { LeadsService } from '../../../core/services/leads.service';
import { PropertyContactService } from '../../../core/services/property-contact.service';
import { PropertiesService } from '../../../core/services/properties.service';
import { selectActor, selectCurrentUser, selectIsAuthenticated } from '../../../store/auth/auth.selectors';
import { ExternalViewerDialogService } from '../external-viewer-dialog/external-viewer-dialog.service';
import { buildImageCandidates } from '../../utils/property-image.util';
import { NotificationService } from '../../../services/notification.service';
import { PROPERTY_STATUS_EDIT_OPTIONS, propertyStatusEditValue, propertyStatusKey, propertyStatusLabel, shouldShowPropertyStatusBadge } from '../../utils/property-status.util';
import { propertyHasGarden, propertyHasTerrace } from '../../utils/property-features.util';

@Component({
    selector: 'app-property-card',
    standalone: true,
    imports: [CommonModule, FormsModule, RouterLink, LucideIconsModule],
    templateUrl: './property-card.component.html',
    styleUrls: ['./property-card.component.scss'],
})
export class PropertyCardComponent implements OnInit, OnDestroy, OnChanges {
    @Input() property!: Property;
    @Input() detailMode: 'public' | 'developer' | 'admin' = 'public';
    @Input() showSendOfferButton = false;
    @Output() sendOffer = new EventEmitter<Property>();

    private store = inject(Store);
    private router = inject(Router);
    private leadsService = inject(LeadsService);
    private propertyContact = inject(PropertyContactService);
    private propertiesService = inject(PropertiesService);
    private currencyService = inject(CurrencyService);
    private viewerDialog = inject(ExternalViewerDialogService);
    private notifications = inject(NotificationService);
    private destroy$ = new Subject<void>();

    showPlans = false;
    showMessageMenu = false;

    imageFailed: Record<string, boolean> = {};
    imageAttemptByPropertyId: Record<string, number> = {};
    imageLoadedByPropertyId: Record<string, boolean> = {};
    imageRetryByPropertyId: Record<string, number> = {};
    private static readonly IMAGE_MAX_RETRIES = 2;
    private imageRetryTimers: Record<string, ReturnType<typeof setTimeout>> = {};

    isAuthenticated: boolean | null = null;
    actor: any | null = null;
    currentUser: any | null = null;

    contactLoading = false;
    contactError: string | null = null;
    private contactFetched = false;
    pendingContactAction: 'whatsapp' | 'email' | null = null;
    private vendorEmail: string | null = null;
    private vendorPhone: string | null = null;
    updatingStatus = false;
    readonly propertyStatusOptions = PROPERTY_STATUS_EDIT_OPTIONS;

    ngOnInit(): void {
        this.store.select(selectIsAuthenticated).pipe(takeUntil(this.destroy$)).subscribe(v => (this.isAuthenticated = v));
        this.store.select(selectActor).pipe(takeUntil(this.destroy$)).subscribe(v => (this.actor = v));
        this.store.select(selectCurrentUser).pipe(takeUntil(this.destroy$)).subscribe(v => (this.currentUser = v));
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['property']) {
            this.showMessageMenu = false;
            this.contactLoading = false;
            this.contactError = null;
            this.contactFetched = false;
            this.pendingContactAction = null;
            this.vendorEmail = null;
            this.vendorPhone = null;
            this.imageFailed = {};
            this.imageAttemptByPropertyId = {};
            this.imageLoadedByPropertyId = {};
            this.imageRetryByPropertyId = {};
            Object.values(this.imageRetryTimers).forEach(t => clearTimeout(t));
            this.imageRetryTimers = {};
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        Object.values(this.imageRetryTimers).forEach(t => clearTimeout(t));
        this.imageRetryTimers = {};
    }

    private permissionSet(): Set<string> {
        const codes = this.currentUser?.permission_codes ?? this.currentUser?.permissionCodes ?? [];
        return new Set(Array.isArray(codes) ? codes.map((value: any) => String(value || '').trim().toLowerCase()).filter(Boolean) : []);
    }

    get canEditStatus(): boolean {
        if (this.detailMode !== 'developer') return false;
        if (this.actor?.actor_type !== 'org') return false;
        const perms = this.permissionSet();
        return perms.has('properties.fields.status:edit') || perms.has('properties:approve');
    }

    get offersFeatureEnabled(): boolean {
        return this.property?.feature_flags?.['offers_enabled'] !== false;
    }

    get contactOwnerFeatureEnabled(): boolean {
        return this.property?.feature_flags?.['contact_owner_enabled'] !== false;
    }

    onSendOffer(event?: Event): void {
        event?.stopPropagation();
        if (!this.showSendOfferButton || !this.offersFeatureEnabled) return;
        this.sendOffer.emit(this.property);
    }

    get isUserPhoneVerified(): boolean {
        const user = this.currentUser || {};
        const direct = user?.phone_verified ?? user?.PHONE_VERIFIED ?? null;
        if (direct !== null && direct !== undefined) return Boolean(direct);
        const at = user?.phone_verified_at ?? user?.PHONE_VERIFIED_AT ?? null;
        return Boolean(at);
    }

    changePropertyStatus(status: string, event?: Event): void {
        event?.stopPropagation();
        if (this.updatingStatus) return;
        const propertyId = String(this.property?.id || '').trim();
        const next = String(status || '').trim();
        if (!propertyId || !next) return;
        if (next === String(this.property?.status || '').trim()) return;

        this.updatingStatus = true;
        this.propertiesService.updatePropertyStatus(propertyId, next).subscribe({
            next: (result) => {
                const updatedStatus = String(result?.status || next).trim();
                this.property = {
                    ...this.property,
                    status: updatedStatus,
                    statusBadge: this.statusBadgeFromValue(updatedStatus),
                    updated_at: result?.updatedAt ?? this.property?.updated_at ?? null
                };
                this.notifications.showSuccess('Property status updated.').subscribe();
            },
            error: (err: any) => {
                const message = err?.error?.error?.message || err?.error?.message || err?.message || 'Failed to update property status';
                this.notifications.showError(message).subscribe();
            },
            complete: () => {
                this.updatingStatus = false;
            }
        });
    }

    get contactLoaded(): boolean {
        return this.contactFetched;
    }

    get vendorEmailAddress(): string | null {
        const candidate = (this.vendorEmail || this.property?.vendorEmail || '').toString().trim();
        return candidate && candidate.includes('@') ? candidate : null;
    }

    get vendorWhatsappNumber(): string | null {
        const raw = (this.vendorPhone || this.property?.vendorPhone || '').toString().trim();
        if (!raw) return null;
        const digits = raw.replace(/[^\d]/g, '');
        if (!digits) return null;
        if (digits.length < 7 || digits.length > 15) return null;
        return digits;
    }

    get detailLink(): any[] {
        if (this.detailMode === 'developer') return ['/developer/properties/view', this.property?.id];
        if (this.detailMode === 'admin') return ['/admin/properties/view', this.property?.id];
        return ['/properties', this.property?.id];
    }

    get publicDetailLink(): any[] {
        return ['/properties', this.property?.id];
    }

    goToDetail(): void {
        this.router.navigate(this.detailLink).catch(() => { });
    }

    get showUnitNumberLine(): boolean {
        const unit = (this.property?.unitNumber || '').toString().trim();
        if (!unit) return false;
        const title = (this.property?.title || '').toString().toLowerCase();
        return !title.includes(unit.toLowerCase());
    }

    get floorLabel(): string | null {
        const raw = this.property?.floor;
        if (!raw) return null;
        const v = String(raw).trim();
        if (!v) return null;
        const lower = v.toLowerCase();
        if (lower === '0' || lower === '0.0' || lower === 'gf' || lower.includes('ground')) return 'Ground';
        const normalized = lower.match(/^(?:floor\\s*)?(\\d+)(?:st|nd|rd|th)?(?:\\s*floor)?$/);
        if (normalized?.[1]) {
            const n = parseInt(normalized[1], 10);
            if (Number.isFinite(n)) return `Floor ${n}`;
        }
        const wordMatch = lower.match(/^(?:the\\s+)?(first|second|third|fourth|fifth|sixth|seventh|eighth|ninth|tenth|eleventh|twelfth|thirteenth|fourteenth|fifteenth|sixteenth|seventeenth|eighteenth|nineteenth|twentieth)(?:\\s+floor)?$/);
        if (wordMatch?.[1]) {
            const map: Record<string, number> = {
                first: 1,
                second: 2,
                third: 3,
                fourth: 4,
                fifth: 5,
                sixth: 6,
                seventh: 7,
                eighth: 8,
                ninth: 9,
                tenth: 10,
                eleventh: 11,
                twelfth: 12,
                thirteenth: 13,
                fourteenth: 14,
                fifteenth: 15,
                sixteenth: 16,
                seventeenth: 17,
                eighteenth: 18,
                nineteenth: 19,
                twentieth: 20
            };
            const n = map[wordMatch[1]];
            if (Number.isFinite(n)) return `Floor ${n}`;
        }
        // For values like "Basement", "Penthouse", etc.
        return v;
    }

    get numberOfFloorsValue(): string | null {
        const raw = this.property?.numberOfFloors;
        if (!raw) return null;
        const v = String(raw).trim();
        return v ? v : null;
    }

    get floorDisplayLabel(): string | null {
        // Cards should show the unit's own floor, not the building's total levels.
        return this.floorLabel;
    }

    get brochureLink(): string | null {
        return this.normalizeExternalUrl(this.property?.brochureUrl);
    }

    get brochureFileId(): string | null {
        const raw = (this.property?.brochureFileId || '').toString().trim();
        return raw ? raw : null;
    }

    get masterPlanLink(): string | null {
        return this.normalizeExternalUrl(this.property?.masterPlanUrl);
    }

    get masterPlanFileId(): string | null {
        const raw = (this.property?.masterPlanFileId || '').toString().trim();
        return raw ? raw : null;
    }

    openDocument(kind: 'brochure' | 'masterPlan', event?: Event): void {
        event?.stopPropagation();
        const title = kind === 'brochure' ? 'Brochure' : 'Master Plan';
        const fileId = kind === 'brochure' ? this.brochureFileId : this.masterPlanFileId;
        const src = kind === 'brochure' ? this.brochureLink : this.masterPlanLink;
        const projectId = this.property?.project_id ?? null;
        if (fileId) {
            this.viewerDialog.open({ fileId, title, projectId });
            return;
        }
        this.viewerDialog.openFromUrl(src, title, projectId ?? undefined);
    }

    viewerQueryParams(url: string | null | undefined, title: string): Record<string, string> {
        const src = String(url || '').trim();
        if (!src) return { title };
        const fileId = this.extractFileIdFromStratusUrl(src);
        if (fileId) return { fileId, title };
        return { src, title };
    }

    openViewer(url: string | null | undefined, title: string, event?: Event): void {
        event?.stopPropagation();
        const projectId = this.property?.project_id ?? undefined;
        this.viewerDialog.openFromUrl(url, title, projectId);
    }

    get hasDiscount(): boolean {
        return typeof this.property?.discountRate === 'number' && Number.isFinite(this.property.discountRate) && (this.property.discountRate || 0) > 0;
    }

    get statusBadge(): Property['statusBadge'] {
        return this.property?.statusBadge || 'unknown';
    }

    get showStatusBadge(): boolean {
        const role = String(this.actor?.role_code || this.currentUser?.role_code || this.currentUser?.role || '').trim().toLowerCase();
        return shouldShowPropertyStatusBadge(this.statusBadge || this.property?.status, role === 'app_admin');
    }

    // Canonical capitalized value for the status <select> (its options are
    // `Available`/`ReservedOrHold`/… while the API returns lowercase codes, so
    // binding the raw value would leave the select blank).
    get statusEditValue(): string {
        return propertyStatusEditValue(this.statusBadge || this.property?.status);
    }

	    get statusLabel(): string {
        return propertyStatusLabel(this.statusBadge || this.property?.status);
    }

  get developmentBadgePills(): Array<{ label: string; title: string; className: string }> {
    const pills: Array<{ label: string; title: string; className: string }> = [];
    const seen = new Set<string>();
    const pushPill = (label: string | null | undefined, className: string) => {
      const normalized = String(label || '').trim();
      if (!normalized) return;
      const key = normalized.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      pills.push({
        label: normalized,
        title: normalized,
        className
      });
    };

    const development = (this.property?.developmentBadge || '').toString().trim();
    if (development) {
      pushPill(development, '!bg-black/60 !text-white !border-white/10 backdrop-blur');
    }

    pushPill(this.property?.cardBadge1 || null, '!bg-white/95 !text-slate-900 !border-white/50 backdrop-blur');
    pushPill(this.property?.cardBadge2 || null, '!bg-emerald-500/90 !text-white !border-emerald-300/40 backdrop-blur');

    const badgeLabel1 = (this.property?.cardBadge1 || '').toString().trim().toLowerCase();
    const badgeLabel2 = (this.property?.cardBadge2 || '').toString().trim().toLowerCase();
    if (propertyHasGarden(this.property) && !badgeLabel1.includes('garden') && !badgeLabel2.includes('garden')) {
      pushPill('Garden', '!bg-white/95 !text-slate-900 !border-white/50 backdrop-blur');
    }
    if (propertyHasTerrace(this.property) && !badgeLabel1.includes('terrace') && !badgeLabel2.includes('terrace')) {
      pushPill('Terrace', '!bg-emerald-500/90 !text-white !border-emerald-300/40 backdrop-blur');
    }

    return pills.slice(0, 3);
  }

    get formattedPrice(): string | null {
        return this.formatCurrency(this.cardDisplayPrice);
    }

    get formattedPriceParts(): { currency: string; amount: string } | null {
        return this.formatCurrencyParts(this.cardDisplayPrice);
    }

    get pricePerSqm(): string | null {
        const area = this.property?.bua ?? this.property?.totalArea ?? this.property?.area;
        const price = this.cardDisplayPrice;
        if (!area || area <= 0 || !price) return null;
        return this.formatCurrency(price / area);
    }

    private get cardDisplayPrice(): number | null {
        const display = this.property?.publicDisplayPrice;
        if (typeof display === 'number' && Number.isFinite(display) && display > 0) return display;
        return this.property?.price ?? null;
    }

    get displayAreaUom(): string {
        const prop = this.property;
        if (!prop) return 'm²';
        const bua = prop.bua ?? null;
        const totalArea = prop.totalArea ?? null;
        const area = prop.area ?? null;
        if (typeof bua === 'number' && bua > 0) return (prop.buaUnit || prop.totalAreaUnit || 'm²').toString();
        if (typeof totalArea === 'number' && totalArea > 0) return (prop.totalAreaUnit || prop.buaUnit || 'm²').toString();
        if (typeof area === 'number' && area > 0) return (prop.totalAreaUnit || prop.buaUnit || 'm²').toString();
        return (prop.buaUnit || prop.totalAreaUnit || 'm²').toString();
    }

    get sanitizedDescription(): string {
        return (this.property?.description || '').replace(/<[^>]*>/g, '').replace(/"/g, '');
    }

    get primaryPlanDisplay() {
        if (this.detailMode === 'public') return null;
        const primary = this.primaryPlan();
        if (!primary) return null;
        const summary = this.formatPlanSummary(primary);
        return {
            ...summary,
            reserve: summary.reserve ?? this.formatCurrency(primary.reservationAmount)
        };
    }

    get additionalPlans() {
        const [, ...rest] = this.sortedPlans();
        return rest.map(plan => this.formatPlanSummary(plan));
    }

    @HostListener('document:click')
    closeMenus() {
        if (this.showPlans) this.showPlans = false;
        if (this.showMessageMenu) this.showMessageMenu = false;
    }

    togglePlans(event?: Event) {
        event?.stopPropagation();
        this.showMessageMenu = false;
        this.showPlans = !this.showPlans;
    }

    toggleMessageMenu(event?: Event) {
        event?.stopPropagation();
        this.showPlans = false;
        this.showMessageMenu = !this.showMessageMenu;
        // Prefetch while the menu opens so the WhatsApp/Email click itself is
        // synchronous and the new tab isn't treated as a popup.
        if (this.showMessageMenu) {
            this.ensureContactLoaded();
        }
    }

    onInternalContactOption(kind: 'whatsapp' | 'email', event?: Event): void {
        event?.stopPropagation();
        this.contactError = null;
        this.pendingContactAction = kind;
        if (kind === 'email' && this.vendorEmailAddress) {
            this.executePendingContactAction();
            return;
        }
        if (kind === 'whatsapp' && this.vendorWhatsappNumber) {
            this.executePendingContactAction();
            return;
        }
        if (!this.contactFetched && !this.contactLoading) {
            this.ensureContactLoaded();
            return;
        }
        this.executePendingContactAction();
    }

    onMessageOption(kind: 'whatsapp' | 'email', event?: Event): void {
        event?.stopPropagation();

        const propertyId = (this.property?.id || '').toString().trim();
        if (!propertyId) return;

        // No sign-up wall and no phone-verification wall: the channel opens
        // immediately. Only a signed-in click can be attributed to a lead.
        if (this.isAuthenticated === true) {
            this.leadsService.recordLead(propertyId, kind).subscribe();
        }

        this.pendingContactAction = kind;
        if (!this.contactFetched && !this.contactLoading) {
            this.ensureContactLoaded();
            return;
        }
        this.executePendingContactAction();
    }

    get whatsappLink(): string {
        const text = encodeURIComponent(this.messageText());
        const phone = this.vendorWhatsappNumber;
        return phone ? `https://wa.me/${phone}?text=${text}` : `https://wa.me/?text=${text}`;
    }

    get emailLink(): string {
        const to = encodeURIComponent(this.vendorEmailAddress || '');
        const unit = (this.property?.unitNumber || '').toString().trim();
        const subjectSuffix = unit ? ` · Unit ${unit}` : '';
        const subject = encodeURIComponent(`nibni inquiry: ${this.property?.title || 'Property'}${subjectSuffix}`);
        const body = encodeURIComponent(this.messageText());
        return `mailto:${to}?subject=${subject}&body=${body}`;
    }

    mainImage(property: Property): string | null {
        const key = property?.id || '';
        if (key && this.imageFailed[key]) return null;
        const candidates = this.propertyImageCandidates(property);
        if (!candidates.length) return null;
        const candidateIndex = Math.max(0, this.imageAttemptByPropertyId[key] || 0);
        const base = candidates[candidateIndex] || null;
        if (!base) return null;
        // Append a retry nonce to force a fresh HTTP request on retry. The
        // /image/:fileId endpoint strips unknown query params, so this doesn't
        // change server behavior — it just prevents the browser from reusing
        // a cached failure response.
        const retryNonce = this.imageRetryByPropertyId[key] || 0;
        if (retryNonce > 0) {
            const sep = base.includes('?') ? '&' : '?';
            return `${base}${sep}_retry=${retryNonce}`;
        }
        return base;
    }

    isImageLoaded(property: Property): boolean {
        const key = String(property?.id || '').trim();
        return key ? Boolean(this.imageLoadedByPropertyId[key]) : false;
    }

    onImageLoad(property: Property): void {
        const key = String(property?.id || '').trim();
        if (!key) return;
        this.imageLoadedByPropertyId[key] = true;
        this.imageRetryByPropertyId[key] = 0;
        const timer = this.imageRetryTimers[key];
        if (timer) {
            clearTimeout(timer);
            delete this.imageRetryTimers[key];
        }
    }

    private ensureContactLoaded(): void {
        if (this.contactLoading || this.contactFetched) return;
        const propertyId = (this.property?.id || '').toString().trim();
        if (!propertyId) return;

        this.contactLoading = true;
        this.contactError = null;
        this.propertyContact.resolveContact(propertyId, { authenticated: this.isAuthenticated === true }).subscribe({
            next: (contact) => {
                this.contactLoading = false;
                this.contactFetched = true;
                this.vendorEmail = contact.vendorEmail;
                this.vendorPhone = contact.vendorPhone;
                // The originating click has already returned, so a new tab would
                // count as an unsolicited popup — navigate this one instead.
                this.executePendingContactAction(true);
            },
            error: (err) => {
                this.contactLoading = false;
                this.contactFetched = true;
                this.vendorEmail = null;
                this.vendorPhone = null;
                this.pendingContactAction = null;
                this.contactError = String(err?.error?.error?.message || err?.error?.message || err?.message || 'Failed to load contact details');
            }
        });
    }

    private executePendingContactAction(redirectCurrentTab = false): void {
        if (!this.pendingContactAction) return;
        if (this.contactLoading) return;

        const action = this.pendingContactAction;
        this.pendingContactAction = null;

        if (action === 'whatsapp') {
            const phone = this.vendorWhatsappNumber;
            if (!phone) {
                this.contactError = 'WhatsApp contact is not available.';
                return;
            }
            if (redirectCurrentTab) {
                window.location.href = this.whatsappLink;
                this.showMessageMenu = false;
                return;
            }
            try {
                window.open(this.whatsappLink, '_blank', 'noopener,noreferrer');
            } catch {
                // fallback: let the browser handle navigation
                window.location.href = this.whatsappLink;
            }
            this.showMessageMenu = false;
            return;
        }

        const email = this.vendorEmailAddress;
        if (!email) {
            this.contactError = 'Email contact is not available.';
            return;
        }
        window.location.href = this.emailLink;
        this.showMessageMenu = false;
    }

    private messageText(): string {
        const title = (this.property?.title || 'a property').toString().trim() || 'a property';
        const price = this.formattedPrice || 'Price on request';
        const unit = (this.property?.unitNumber || '').toString().trim();
        const location = (this.locationLabel || '').toString().trim();
        const url = this.publicPropertyUrl();

        const lines = [
            `Hi, I'm interested in ${title}.`,
            unit ? `Unit: ${unit}` : null,
            location ? `Location: ${location}` : null,
            price ? `Price: ${price}` : null,
            url ? `Link: ${url}` : null
        ].filter(Boolean) as string[];

        return lines.join('\n');
    }

    private countryNameFromCode(code: string | null | undefined): string | null {
        const normalized = String(code || '').trim().toUpperCase();
        if (!/^[A-Z]{2}$/.test(normalized)) return null;
        try {
            const display = new Intl.DisplayNames(['en'], { type: 'region' });
            return display.of(normalized) || normalized;
        } catch {
            return normalized;
        }
    }

    get countryLabel(): string | null {
        const raw = (this.property?.countryName || this.property?.country || '').toString().trim();
        if (!raw) return null;
        const upper = raw.toUpperCase();
        if (/^[A-Z]{2}$/.test(upper)) return this.countryNameFromCode(upper) || raw;
        return raw;
    }

    get locationLabel(): string | null {
        const raw = (
            this.property?.locationAddress ||
            this.property?.location ||
            this.property?.countryName ||
            this.property?.country ||
            ''
        )
            .toString()
            .trim();
        return raw ? raw : null;
    }

    get locationMapLink(): string | null {
        const direct = (this.property?.locationLink || '').toString().trim();
        if (direct && /^https?:\/\//i.test(direct)) return direct;

        const label = this.locationLabel;
        if (!label) return null;
        return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(label)}`;
    }

    get statusMeta(): { label: string; className: string } {
        const key = propertyStatusKey(this.statusBadge || this.property?.status);
        const label = propertyStatusLabel(this.statusBadge || this.property?.status);
        const map: Record<string, string> = {
            available: 'bg-emerald-500/90 text-white border-emerald-400/50',
            reserved: 'bg-amber-500/90 text-white border-amber-400/50',
            reservedwithdeposit: 'bg-orange-500/90 text-white border-orange-400/50',
            contracted: 'bg-rose-500/90 text-white border-rose-400/50',
            sold: 'bg-red-500/90 text-white border-red-400/50',
            draft: 'bg-slate-700/80 text-white border-white/10',
            pendingapproval: 'bg-yellow-500/90 text-slate-950 border-yellow-300/70',
            unpublished: 'bg-slate-700/80 text-white border-white/10',
            unknown: 'bg-black/60 text-white border-white/10'
        };
        return { label, className: map[key] || map['unknown'] };
    }

    private statusBadgeFromValue(status: string | null | undefined): Property['statusBadge'] {
        return propertyStatusKey(status);
    }

    get floorIconLabel(): string | null {
        const label = (this.floorLabel || "").toString().trim();
        if (!label) return null;
        const lower = label.toLowerCase();
        if (lower === "ground") return "G";
        const match = lower.match(/floor\\s*(\\d+)/);
        if (match?.[1]) return match[1];
        return label;
    }

    get isFullyFurnished(): boolean {
        const raw = (this.property?.furnishType || '').toString();
        if (raw && /fully\s*furnish/i.test(raw)) return true;

        const features = this.property?.features || [];
        return features.some((feature) => /fully\s*furnish/i.test(String(feature?.name || '')));
    }

    get furnishLabel(): string | null {
        const raw = (this.property?.furnishType || "").toString().trim();
        if (!raw) return null;
        const lower = raw.toLowerCase();
        if (["no", "none", "not furnished", "unfurnished"].includes(lower)) return "No";
        if (["yes", "furnished"].includes(lower)) return "Yes";
        return raw;
    }

    get finishingLabel(): string | null {
        const raw = (this.property?.finishingType || "").toString().trim();
        return raw ? raw : null;
    }

    private publicPropertyUrl(): string {
        const id = (this.property?.id || '').toString().trim();
        if (!id) return '';
        const route = `/properties/${encodeURIComponent(id)}`;
        if (typeof window === 'undefined') return `/app/#${route}`;
        const origin = window.location?.origin || '';
        return origin ? `${origin}/app/#${route}` : `/app/#${route}`;
    }

    handleImageError(property: Property, currentUrl?: string | null): void {
        const key = String(property?.id || '').trim();
        if (!key) return;
        delete this.imageLoadedByPropertyId[key];

        // Retry the same URL a few times before advancing to the next candidate.
        // This handles transient backend failures (Catalyst concurrency-limit
        // 429s, cold starts) where the same URL would succeed on a second attempt.
        const retryCount = this.imageRetryByPropertyId[key] || 0;
        if (retryCount < PropertyCardComponent.IMAGE_MAX_RETRIES) {
            const delay = 400 * (retryCount + 1);
            this.imageRetryTimers[key] = setTimeout(() => {
                // Increment the nonce INSIDE the timeout so the re-request is
                // delayed (gives the server time to recover from a concurrency
                // spike). zone.js triggers change detection after this callback,
                // causing mainImage() to re-evaluate with the new nonce → the
                // <img> src changes → the browser makes a fresh request.
                this.imageRetryByPropertyId[key] = retryCount + 1;
            }, delay);
            return;
        }

        // Exhausted retries for this candidate — advance to the next one.
        this.imageRetryByPropertyId[key] = 0;
        const candidates = this.propertyImageCandidates(property);
        const candidateIndex = Math.max(0, this.imageAttemptByPropertyId[key] || 0);
        if (candidateIndex + 1 < candidates.length) {
            this.imageAttemptByPropertyId[key] = candidateIndex + 1;
            return;
        }
        this.imageFailed[key] = true;
    }

    private propertyImageCandidates(property: Property): string[] {
        return buildImageCandidates(property?.mainImage, property?.images);
    }

    private normalizeExternalUrl(url?: string | null): string | null {
        if (!url) return null;
        const trimmed = String(url).trim();
        if (!trimmed) return null;
        const lower = trimmed.toLowerCase();
        if (lower.includes('pending.local')) return null;
        if (!lower.startsWith('http://') && !lower.startsWith('https://')) return null;
        return trimmed;
    }

    private extractFileIdFromStratusUrl(value: string): string | null {
        const raw = String(value || '').trim();
        if (!raw) return null;
        try {
            const url = new URL(raw);
            const path = url.pathname || '';
            const match =
                path.match(/\/files\/([0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})(?:\/|$)/i)
                || path.match(/\/public\/([0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})(?:\/|$)/i);
            return match?.[1] ? String(match[1]) : null;
        } catch {
            return null;
        }
    }

    private sortedPlans(): PaymentPlan[] {
        return [...(this.property?.paymentPlans || [])]
            .filter(plan =>
                this.hasPlanNumber(plan.totalPeriod)
                || this.hasPlanNumber(plan.downPayment)
                || this.hasPlanNumber(plan.downPaymentPercent)
                || this.hasPlanNumber(plan.reservationAmount)
                || this.hasPlanNumber(plan.reservationPercent)
                || this.hasPlanNumber(plan.installmentAmount)
            )
            .sort((a, b) => (a.totalPeriod ?? 0) - (b.totalPeriod ?? 0));
    }

    private primaryPlan(): PaymentPlan | null {
        const [primary] = this.sortedPlans();
        return primary || null;
    }

    private formatPlanSummary(plan: PaymentPlan) {
        const label = this.formatPlanLabel(plan.totalPeriod) ?? 'Installment Plan';
        const price = this.formatCurrency(plan.displayPrice ?? null);
        const down = this.formatContribution(plan.downPayment, plan.downPaymentPercent);
        const installment = this.formatCurrency(plan.installmentAmount);
        const reserve = this.formatContribution(plan.reservationAmount, plan.reservationPercent);
        const { suffix, description } = this.formatCycleInfo(plan.paymentCycle);
        const installmentsCount = this.hasPlanNumber(plan.numberOfInstallments)
            ? `${plan.numberOfInstallments} installment${plan.numberOfInstallments === 1 ? '' : 's'}`
            : null;

        return {
            label,
            price,
            down,
            reserve,
            installment: installment ? `${installment}${suffix}` : null,
            cycleDescription: description,
            installmentsCount
        };
    }

    private hasPlanNumber(value: unknown): value is number {
        return typeof value === 'number' && Number.isFinite(value);
    }

    private formatCurrency(value?: number | null): string | null {
        if (value === null || value === undefined || !Number.isFinite(value)) return null;
        const fromCode = this.property?.currency || this.property?.currencyInfo?.code || 'USD';
        const display = this.currencyService.convertForDisplay(value, fromCode);
        const converted = display.amount;
        return this.currencyService.format(converted, display.currency);
    }

    private formatCurrencyParts(value?: number | null): { currency: string; amount: string } | null {
        if (value === null || value === undefined || !Number.isFinite(value)) return null;
        const fromCode = this.property?.currency || this.property?.currencyInfo?.code || 'USD';
        const display = this.currencyService.convertForDisplay(value, fromCode);
        const converted = display.amount;
        const currencyCode = display.currency;

        try {
            const parts = new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: currencyCode,
                currencyDisplay: 'code',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
            }).formatToParts(converted);

            const currency = (parts.find(p => p.type === 'currency')?.value || currencyCode)
                .replace(/[\u00A0\u202F]/g, ' ')
                .trim();
            const amount = parts
                .filter(p => p.type !== 'currency')
                .map(p => p.value)
                .join('')
                .replace(/[\u00A0\u202F]/g, ' ')
                .trim();

            return { currency, amount };
        } catch {
            const formatted = this.currencyService.format(converted, currencyCode);
            const match = formatted.match(/^(\S+)\s+(.+)$/);
            if (match) return { currency: match[1], amount: match[2] };
            return { currency: currencyCode, amount: formatted };
        }
    }

    private resolvePlanContribution(amount?: number | null, percent?: number | null) {
        const price = this.property?.price ?? null;
        const pct = this.hasPlanNumber(percent)
            ? percent
            : (price && this.hasPlanNumber(amount) ? (amount / price) * 100 : null);
        const amt = this.hasPlanNumber(amount)
            ? amount
            : (price && this.hasPlanNumber(pct) ? (price * (pct as number)) / 100 : null);
        return {
            amount: this.hasPlanNumber(amt) ? (amt as number) : null,
            percent: this.hasPlanNumber(pct) ? parseFloat((pct as number).toFixed(2)) : null
        };
    }

    private formatContribution(amount?: number | null, percent?: number | null): string | null {
        const { amount: amt, percent: pct } = this.resolvePlanContribution(amount, percent);
        const pctLabel = pct !== null ? `${this.trimTrailingZeros(pct)}%` : null;
        const amountLabel = this.formatCurrency(amt);
        if (pctLabel && amountLabel) return `${pctLabel} (${amountLabel})`;
        return pctLabel || amountLabel;
    }

    private trimTrailingZeros(value: number): string {
        const fixed = value.toFixed(2);
        return fixed.replace(/\.?0+$/, '');
    }

    private formatPlanLabel(totalPeriod?: number | null): string | null {
        if (!this.hasPlanNumber(totalPeriod) || totalPeriod <= 0) return null;
        const years = totalPeriod / 12;
        if (years >= 1) {
            const display = Number.isInteger(years) ? years : parseFloat(years.toFixed(1));
            return `${display} Year Plan`;
        }
        return `${totalPeriod} Month${totalPeriod !== 1 ? 's' : ''} Plan`;
    }

	    private formatCycleInfo(cycle?: number | null): { suffix: string; description: string | null } {
	        if (!this.hasPlanNumber(cycle) || cycle <= 0) {
	            return { suffix: '', description: null };
	        }

	        // Backward-compatible: historically some records stored payment_cycle in days (30/90/365).
	        // We normalize to months for display.
	        const raw = cycle;
	        const monthsRaw = raw > 24 ? raw / 30 : raw;
	        const months = parseFloat(monthsRaw.toFixed(1));

	        const isYear =
	            (raw <= 24 && raw === 12)
	            || (raw > 24 && months >= 11.5 && months <= 12.5);
	        if (isYear) return { suffix: '/yr', description: 'per year' };

	        if (months >= 2.9 && months <= 3.1) return { suffix: '/Quarterly', description: 'Quarterly' };
	        if (months >= 0.9 && months <= 1.1) return { suffix: '/mo', description: 'per month' };

	        const suffix = `/${months}mo`;
	        const description = `every ${months} month${months === 1 ? '' : 's'}`;
	        return { suffix, description };
	    }
}
